import React, { useContext } from 'react';
import PageLayout from '../../pagelayout';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../../components/table/alpTable';
import { AdminApiContext } from '../../api/AdminApiContext';
import { useLoader } from '../../api/useLoader';
import {
  BrukerPraksisstedDto,
  BrukerStudiestedDto,
  BrukerStudiestedtilgangDto,
} from '../../api/generated/admin-generated-api';
import Spinner from '../../components/spinner/spinner';
import translations from './myUsers.i18n';
import PanelHeader from '../../components/panelheader/panelHeader';
import Button from '../../components/button';
import * as css from './myUsers.module.scss';
import useAddErrorNotificationCallback from '../../hooks/useAddErrorNotificationCallback';
import { NotificationFunctionContext } from '../../components/notifications/notificationProvider';
import { BrukerPraksisstedtilgangDto } from '../../api/generated/admin-generated-api/api';
import { useFormatMessageFor } from '../../i18n/translation';

enum UpdateAction {
  Deaktiver,
  Reaktiver,
  ResetTilgang,
}

const mapUpdateActionToQueryParams = (
  updateAction: UpdateAction,
): {
  deaktiver?: true;
  reaktiver?: true;
  resetTilgang?: true;
} => {
  switch (updateAction) {
    case UpdateAction.ResetTilgang:
      return { resetTilgang: true };
    case UpdateAction.Deaktiver:
      return { deaktiver: true };
    case UpdateAction.Reaktiver:
      return { reaktiver: true };
  }
};

const MyUsersPage = () => {
  const { brukerApi } = useContext(AdminApiContext);

  const {
    data: brukere,
    loading,
    reload,
  } = useLoader(() => brukerApi.minebrukereGet(), []);

  const { addSuccessNotification } = useContext(NotificationFunctionContext);
  const errorNotificationCallback = useAddErrorNotificationCallback();

  const labels = useFormatMessageFor(translations);

  if (loading) return <Spinner />;
  if (!brukere) return <></>;

  const responseHandler = (response: Promise<void>) => {
    response
      .then(() => {
        addSuccessNotification(labels.success);
        reload();
      })
      .catch(errorNotificationCallback);
  };

  return (
    <PageLayout
      title={labels.pageTitle}
      subtitle={labels.pageSubtitle}
      isIndented
    >
      <PanelHeader title={labels.studiestedbrukere} collapsible>
        <UserTableWrapper
          brukere={brukere.studiestedbrukere}
          updateFunc={(studiestedId, updateAction) =>
            responseHandler(
              brukerApi.studiestedbrukerStudiestedIdPut({
                pathParams: { studiestedId },
                queryParams: mapUpdateActionToQueryParams(updateAction),
              }),
            )
          }
        />
      </PanelHeader>
      <PanelHeader title={labels.praksisstedbrukere} collapsible>
        <UserTableWrapper
          brukere={brukere.praksisstedbrukere}
          updateFunc={(praksisstedId, updateAction) =>
            responseHandler(
              brukerApi.praksisstedbrukerPraksisstedIdPut({
                pathParams: { praksisstedId },
                queryParams: mapUpdateActionToQueryParams(updateAction),
              }),
            )
          }
        />
      </PanelHeader>
    </PageLayout>
  );
};

const mapTilgangToStednavn = (
  tilgang: BrukerPraksisstedtilgangDto | BrukerStudiestedtilgangDto,
): string => {
  const asPraksisstedTilgang = tilgang as BrukerPraksisstedtilgangDto;
  const asStudiestedTilgang = tilgang as BrukerStudiestedtilgangDto;
  if (asPraksisstedTilgang?.navnPraksissted) {
    return asPraksisstedTilgang.navnPraksissted;
  } else if (asStudiestedTilgang?.navnStudiested) {
    return asStudiestedTilgang.navnStudiested;
  }

  return '';
};

const mapTilgangerToString = (
  tilganger: (BrukerPraksisstedtilgangDto | BrukerStudiestedtilgangDto)[],
): string =>
  tilganger
    .map(
      (tilgang) =>
        `${tilgang.navnToppnode} - ${mapTilgangToStednavn(tilgang)} (${
          tilgang.rolle
        })`,
    )
    .join(';');

const UserTableHeaders = {
  FORNAVN: 'FORNAVN',
  ETTERNAVN: 'ETTERNAVN',
  EPOST: 'EPOST',
  TILGANGER: 'TILGANGER',
  HANDLINGER: 'HANDLINGER',
} as const;

const useTableHeaderToTranslations = (): {
  [key in keyof typeof UserTableHeaders]: string;
} => {
  const labels = useFormatMessageFor(translations);

  return {
    FORNAVN: labels.headerFornavn,
    ETTERNAVN: labels.headerEtternavn,
    EPOST: labels.headerEpost,
    TILGANGER: labels.headerTilganger,
    HANDLINGER: labels.headerHandlinger,
  };
};

type Bruker = BrukerPraksisstedDto | BrukerStudiestedDto;

interface UserTableWrapperProps {
  brukere: Bruker[];
  updateFunc: (stedId: string, type: UpdateAction) => void;
}

const UserTableWrapper: React.FC<
  React.PropsWithChildren<UserTableWrapperProps>
> = ({ brukere, updateFunc }) => {
  const headerTranslations = useTableHeaderToTranslations();
  const labels = useFormatMessageFor(translations);

  const rows = brukere.map((bruker) => (
    <TableRow key={bruker.id}>
      <TableCellTd header={headerTranslations[UserTableHeaders.FORNAVN]}>
        {bruker.fornavn}
      </TableCellTd>
      <TableCellTd header={headerTranslations[UserTableHeaders.ETTERNAVN]}>
        {bruker.etternavn}
      </TableCellTd>
      <TableCellTd header={headerTranslations[UserTableHeaders.EPOST]}>
        {bruker.epost}
      </TableCellTd>
      <TableCellTd header={headerTranslations[UserTableHeaders.TILGANGER]}>
        {mapTilgangerToString(bruker.tilganger)}
      </TableCellTd>
      <TableCellTd
        header={headerTranslations[UserTableHeaders.HANDLINGER]}
        className={css.actionCell}
      >
        {/*
         * bruker.tilganger[0] is used, as all functionality is considered for
         * the user in general and not for each access level/point
         */}
        <div className={css.content}>
          <Button.Tertiary
            onClick={() =>
              updateFunc(bruker.tilganger[0].id, UpdateAction.ResetTilgang)
            }
            disabled={bruker.administratorToppnivaa}
          >
            {labels.btnReset}
          </Button.Tertiary>
          <Button.Tertiary
            onClick={() =>
              updateFunc(
                bruker.tilganger[0].id,
                bruker.deaktivert
                  ? UpdateAction.Reaktiver
                  : UpdateAction.Deaktiver,
              )
            }
          >
            {bruker.deaktivert ? labels.btnReaktiver : labels.btnDeaktiver}
          </Button.Tertiary>
        </div>
      </TableCellTd>
    </TableRow>
  ));

  return <UserTable>{rows}</UserTable>;
};

const UserTable: React.FC<React.PropsWithChildren> = ({ children }) => {
  const headerTranslations = useTableHeaderToTranslations();
  const headers = Object.keys(UserTableHeaders).map((header) => (
    <TableCellTh key={header}>
      {headerTranslations[header as keyof typeof UserTableHeaders]}
    </TableCellTh>
  ));

  return (
    <Table>
      <TableHeader>
        <TableRow top>{headers}</TableRow>
      </TableHeader>
      <TableMain>{children}</TableMain>
    </Table>
  );
};

export default MyUsersPage;
